import React, { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Button,
  Chip,
  IconButton,
  Popover,
  Stack,
  TextField,
  TextFieldProps,
  Typography
} from "@mui/material";
import { useRolesSelector } from "store";
import { IRole } from "store/types";
import { IUsersManageQueries } from "store/types/user-management";

import "./Filters.styles.scss";

const commonInputPtops: {
  sx?: TextFieldProps["sx"];
  size: TextFieldProps["size"];
  InputLabelProps: TextFieldProps["InputLabelProps"];
} = {
  size: "small",
  InputLabelProps: { shrink: true }
};

interface IFiltersProps {
  updateQueries: (data: Partial<IUsersManageQueries>) => void;
  appliedRoleIds?: number[];
}

const chipSx = {
  height: "24px",
  borderRadius: "32px",
  backgroundColor: "#f6f8fc",
  color: "#071f35",
  fontSize: "12px",
  ".MuiChip-deleteIcon": { fontSize: "16px", color: "#071f35", "&:hover": { color: "#071f35" } }
} as const;

export const Filters: React.FC<IFiltersProps> = ({ updateQueries, appliedRoleIds }) => {
  const roles = useRolesSelector();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [draftRoleIds, setDraftRoleIds] = useState<number[]>([]);

  const allRoles = [
    ...roles,
    { id: -1, presentation_name: "Contact", name: "Contact", short_name: "Contact" } as unknown as IRole
  ];

  const appliedRoles = allRoles.filter((role) => appliedRoleIds?.includes(role.id));
  const draftRoles = allRoles.filter((role) => draftRoleIds.includes(role.id));
  const hasFilters = Boolean(appliedRoleIds?.length);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDraftRoleIds(appliedRoleIds ?? []); // seed the popover from the currently applied filters
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    updateQueries({ role_id: draftRoleIds.length ? draftRoleIds : undefined });
    handleClose();
  };

  const removeRole = (id: number) => {
    const next = (appliedRoleIds ?? []).filter((roleId) => roleId !== id);
    updateQueries({ role_id: next.length ? next : undefined });
  };

  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: "15px", color: "#071f35" }}>Filter:</Typography>
      <IconButton
        onClick={handleOpen}
        aria-label="add filter"
        sx={{
          color: "#071f35",
          width: "24px",
          height: "24px",
          backgroundColor: "#F6F8FC",
          fontSize: "18px",
          fontWeight: 300,
          lineHeight: 1,
          "&:hover": { backgroundColor: "#e9eef5" }
        }}
      >
        +
      </IconButton>

      {appliedRoles.map((role) => (
        <Chip
          key={role.id}
          label={`Platform Role: ${role.presentation_name}`}
          onDelete={() => removeRole(role.id)}
          deleteIcon={<CloseIcon />}
          sx={chipSx}
        />
      ))}
      {hasFilters && (
        <Typography
          component="button"
          onClick={() => updateQueries({ role_id: undefined })}
          sx={{ fontSize: "12px", color: "#26a6ae", background: "none", border: "none", cursor: "pointer", p: 0 }}
        >
          Clear all
        </Typography>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        sx={{ overflow: "hidden" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <div className="users-filters-popup">
          <Autocomplete
            options={allRoles}
            multiple
            value={draftRoles}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IRole) => (
              <li {...props} key={option.id}>
                {option.presentation_name}
              </li>
            )}
            onChange={(_, option) => setDraftRoleIds([...option].map((val) => val.id))}
            getOptionLabel={(option) => option.presentation_name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                {...commonInputPtops}
                label="Platform Role"
                placeholder={draftRoles.length ? "" : "Select"}
              />
            )}
          />
          <Stack flexDirection="row" justifyContent="flex-end" alignItems="center" gap="10px">
            <Button size="sm" variant="outlined" sx={{ minWidth: "53px" }} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              size="sm"
              variant="contained"
              sx={{ backgroundColor: "#071F35", minWidth: "46px" }}
            >
              Apply
            </Button>
          </Stack>
        </div>
      </Popover>
    </Stack>
  );
};
